import { useCallback, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Database, AlertCircle, Plus } from 'lucide-react';
import { Alert, AlertDescription, DataTable, Pagination, useAuth, Button } from '@aaas/shared';
import { useATSRole } from '../../hooks/useATSRole';
import type { DataTableColumn } from '@aaas/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useUrlPagination } from '../../hooks/useUrlPagination';
import { usePoolDetailQuery } from '../../hooks/usePoolQueries';
import { useCVListQuery } from '../../hooks/useCVQueries';
import type { CV, CVProcessingStatus } from '../../types';
import { PROCESSING_STATUS_CONFIG } from '../../types';
import { AddCVsToPoolDialog } from '../../components/AddCVsToPoolDialog';
import cvService from '../../services/cv.service';

// Status badge component
function StatusBadge({ status, label }: { status: CVProcessingStatus; label: string }) {
  const config = PROCESSING_STATUS_CONFIG[status];
  const colorClasses = {
    gray: 'bg-gray-100 text-gray-700',
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorClasses[config.color]}`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
          config.color === 'gray'
            ? 'bg-gray-500'
            : config.color === 'blue'
              ? 'bg-blue-500'
              : config.color === 'green'
                ? 'bg-green-500'
                : 'bg-red-500'
        }`}
      />
      {label}
    </span>
  );
}

// Chip component
function Chip({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
      {label}
    </span>
  );
}

export function PoolDetailPage() {
  const { t } = useTranslation('ats');
  const getStatusLabel = (status: CVProcessingStatus) =>
    t(`cvSelection.status.${status === 'error' ? 'failed' : status}`);
  const { poolId } = useParams<{ poolId: string }>();
  const navigate = useNavigate();
  const { user, currentWorkspace, idToken } = useAuth();
  const { isLeaderOrAbove } = useATSRole();
  const queryClient = useQueryClient();
  const { page, size, sortBy, order, setPage, setSize, setSort } = useUrlPagination();

  const [showAddCVsDialog, setShowAddCVsDialog] = useState(false);

  // Fetch pool details
  const {
    data: pool,
    isLoading: poolLoading,
    error: poolError,
  } = usePoolDetailQuery({
    poolId,
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    enabled: !!user && !!currentWorkspace && !!poolId,
  });

  // Fetch CVs in this pool using unified CV list query with poolId filter
  const {
    data: cvResponse,
    isLoading: cvsLoading,
    error: cvsError,
  } = useCVListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    page,
    size,
    sortBy,
    order,
    poolIds: poolId ? [poolId] : undefined,
    enabled: !!user && !!currentWorkspace && !!poolId,
    refetchInterval: 5000,
  });

  // Extract data from response
  const cvs = cvResponse?.data?.items || [];
  const totalItems = cvResponse?.data?.total || 0;
  const totalPages = Math.ceil(totalItems / size);

  const handleBack = () => {
    navigate('/ats/pools');
  };

  const handleAddCVs = async (cvIds: string[]) => {
    if (!user || !currentWorkspace || !poolId) throw new Error(t('pool.detail.missingData'));
    const token = await user.getIdToken();
    await cvService.bulkAddCVsToPools(cvIds, currentWorkspace.id, token, [poolId]);
    queryClient.invalidateQueries({ queryKey: ['cvs'] });
  };

  const handleCVClick = useCallback(
    (record: CV) => {
      navigate(`/ats/cvs/${record.id}`);
    },
    [navigate]
  );

  // Define table columns (same as CVListPage)
  const columns: DataTableColumn<CV>[] = [
    {
      key: 'name',
      title: t('pool.detail.columns.name'),
      sortable: true,
      render: (value: string, record: CV) => (
        <button
          onClick={() => handleCVClick(record)}
          className="font-medium text-blue-600 hover:text-blue-700 hover:underline text-left"
        >
          {value}
        </button>
      ),
    },
    {
      key: 'processing_status',
      title: t('pool.detail.columns.status'),
      width: 120,
      render: (value: CVProcessingStatus) => (
        <StatusBadge status={value} label={getStatusLabel(value)} />
      ),
    },
    {
      key: 'pools',
      title: t('pool.detail.columns.pools'),
      render: (_value, record: CV) => {
        if (!record.pools || record.pools.length === 0) {
          return <span className="text-gray-400">-</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {record.pools.map((p) => (
              <Chip key={p.id} label={p.name} />
            ))}
          </div>
        );
      },
    },
    {
      key: 'positions',
      title: t('pool.detail.columns.positions'),
      render: (_value, record: CV) => {
        if (!record.positions || record.positions.length === 0) {
          return <span className="text-gray-400">-</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {record.positions.map((position) => (
              <Chip key={position.id} label={position.name} />
            ))}
          </div>
        );
      },
    },
    {
      key: 'jobs',
      title: t('pool.detail.columns.jobs'),
      render: (_value, record: CV) => {
        if (!record.jobs || record.jobs.length === 0) {
          return <span className="text-gray-400">-</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {record.jobs.map((job) => (
              <Chip
                key={job.id}
                label={`${job.code}${job.campaign_name ? ` (${job.campaign_name})` : ''}`}
              />
            ))}
          </div>
        );
      },
    },
    {
      key: 'created_at',
      title: t('pool.detail.columns.created'),
      width: 120,
      sortable: true,
      render: (value: string) => new Date(value).toLocaleDateString(),
    },
  ];

  const loading = poolLoading || cvsLoading;
  const error = poolError || cvsError;

  if (loading && !pool) {
    return (
      <div className="w-full p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (poolError) {
    return (
      <div className="space-y-6">
        <button
          onClick={handleBack}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('pool.detail.back')}
        </button>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {poolError instanceof Error ? poolError.message : t('pool.detail.loadFailed')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!pool) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={handleBack}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('pool.detail.back')}
        </button>

        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Database className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{pool.name}</h1>
              {pool.description && <p className="text-sm text-gray-500">{pool.description}</p>}
            </div>
          </div>
          {isLeaderOrAbove && (
            <Button onClick={() => setShowAddCVsDialog(true)} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              {t('pool.detail.addCvs')}
            </Button>
          )}
        </div>

        {/* Pool metadata */}
        <div className="mt-4 flex items-center gap-6 text-sm text-gray-600">
          <div>
            <span className="text-gray-400">{t('pool.detail.totalCvs')}</span>{' '}
            <span className="font-medium">{totalItems}</span>
          </div>
          <div>
            <span className="text-gray-400">{t('pool.detail.created')}</span>{' '}
            <span className="font-medium">{new Date(pool.created_at).toLocaleDateString()}</span>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : t('pool.detail.loadCvsFailed')}
          </AlertDescription>
        </Alert>
      )}

      {/* Data Table */}
      <div className="space-y-4">
        <DataTable
          columns={columns}
          data={cvs}
          loading={cvsLoading}
          rowKey={(record) => record.id}
          emptyMessage={t('pool.detail.empty')}
          onSort={setSort}
          sortKey={sortBy}
          sortDirection={order}
        />

        {/* Pagination */}
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          pageSize={size}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={setSize}
          showTotal={true}
          showPageSizeSelector={true}
        />
      </div>

      {/* Add CVs Dialog */}
      <AddCVsToPoolDialog
        open={showAddCVsDialog}
        onClose={() => setShowAddCVsDialog(false)}
        onConfirm={handleAddCVs}
        workspaceId={currentWorkspace?.id}
        idToken={idToken || undefined}
        existingCvIds={cvs.map((cv) => cv.id)}
      />
    </div>
  );
}
